/**
 * <custom-accordion> v3 — animated <details> for Dawn
 *
 * Attributes:
 *   data-group        group id (section.id) — scopes "one open at a time"
 *   data-single-open  "true" to auto-close siblings in the same group
 *   data-duration     animation duration in ms (default 400)
 */
if (!customElements.get('custom-accordion')) {
  class CustomAccordion extends HTMLElement {
    constructor() {
      super();
      this.details = this.querySelector('.custom-accordion__details');
      this.summary = this.querySelector('.custom-accordion__summary');
      this.panel = this.querySelector('.custom-accordion__panel');
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
    }

    connectedCallback() {
      if (!this.details || !this.summary || !this.panel) return;
      this.onClick = this.onClick.bind(this);
      this.summary.addEventListener('click', this.onClick);
      if (this.details.open) this.classList.add('is-open');
      this.markLastInGroup();
    }

    disconnectedCallback() {
      if (this.summary) this.summary.removeEventListener('click', this.onClick);
      if (this.animation) this.animation.cancel();
    }

    get duration() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
      const value = parseInt(this.dataset.duration || '400', 10);
      return Number.isNaN(value) ? 400 : value;
    }

    get easing() {
      return 'cubic-bezier(0.32, 0.72, 0, 1)';
    }

    get groupMembers() {
      const group = this.dataset.group;
      if (!group) return [this];
      return Array.from(document.querySelectorAll(`custom-accordion[data-group="${group}"]`));
    }

    /* Only the visually last item in a group gets a closing border. */
    markLastInGroup() {
      const members = this.groupMembers;
      members.forEach((el) => el.classList.remove('is-last'));
      members[members.length - 1].classList.add('is-last');
    }

    onClick(event) {
      event.preventDefault();
      this.details.style.overflow = 'hidden';

      if (this.isClosing || !this.details.open) {
        this.open();
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }

    open() {
      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      this.classList.add('is-open');
      this.summary.setAttribute('aria-expanded', 'true');
      this.closeSiblings();
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      this.isExpanding = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight + this.panel.offsetHeight}px`;

      if (this.animation) this.animation.cancel();

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: this.duration, easing: this.easing }
      );

      this.panel.animate(
        { opacity: [0, 1], transform: ['translateY(-0.6rem)', 'translateY(0)'] },
        {
          duration: this.duration * 0.8,
          delay: this.duration * 0.2,
          easing: 'ease-out',
          fill: 'backwards',
        }
      );

      this.animation.onfinish = () => this.onAnimationFinish(true);
      this.animation.oncancel = () => {
        this.isExpanding = false;
      };
    }

    shrink() {
      this.isClosing = true;
      this.classList.remove('is-open');
      this.summary.setAttribute('aria-expanded', 'false');

      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;

      if (this.animation) this.animation.cancel();

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: this.duration, easing: this.easing }
      );

      this.panel.animate(
        { opacity: [1, 0] },
        { duration: this.duration * 0.5, easing: 'ease-in', fill: 'forwards' }
      );

      this.animation.onfinish = () => this.onAnimationFinish(false);
      this.animation.oncancel = () => {
        this.isClosing = false;
      };
    }

    onAnimationFinish(open) {
      this.details.open = open;
      this.classList.toggle('is-open', open);
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.details.style.height = '';
      this.details.style.overflow = '';
      this.panel.getAnimations().forEach((a) => a.cancel());
      this.panel.style.opacity = '';
      this.panel.style.transform = '';
    }

    closeSiblings() {
      if (this.dataset.singleOpen !== 'true') return;

      const myTop = this.getBoundingClientRect().top;
      let hasClosingAbove = false;

      this.groupMembers.forEach((el) => {
        if (el === this || !el.details || !el.details.open || el.isClosing) return;
        if (el.getBoundingClientRect().top < myTop) hasClosingAbove = true;
        el.shrink();
      });

      /* Keep the clicked header visually pinned while items above collapse. */
      if (hasClosingAbove) this.stabilizeScroll();
    }

    stabilizeScroll() {
      if (!this.duration) return;
      const anchor = this.getBoundingClientRect().top;
      const maxFrames = Math.ceil(this.duration / 16) + 5;
      let frame = 0;

      const step = () => {
        const delta = this.getBoundingClientRect().top - anchor;
        if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
        if (frame++ < maxFrames) window.requestAnimationFrame(step);
      };

      window.requestAnimationFrame(step);
    }
  }

  customElements.define('custom-accordion', CustomAccordion);
}